import React from "react";
import { Button, Input, Label } from "@/components/primitives";
import { Eye, EyeOff } from "lucide-react";

interface PasswordFormProps {
	newPassword: string;
	confirmNewPassword: string;
	showNewPassword: boolean;
	showConfirmPassword: boolean;
	isLoading: boolean;
	error: string;
	onPasswordChange: (value: string) => void;
	onConfirmPasswordChange: (value: string) => void;
	onToggleShowPassword: () => void;
	onToggleShowConfirmPassword: () => void;
	onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
	onCancel: () => void;
	passwordLabel?: string;
	confirmPasswordLabel?: string;
	submitButtonText?: string;
	cancelButtonText?: string;
}

export function PasswordForm({
	newPassword,
	confirmNewPassword,
	showNewPassword,
	showConfirmPassword,
	isLoading,
	error,
	onPasswordChange,
	onConfirmPasswordChange,
	onToggleShowPassword,
	onToggleShowConfirmPassword,
	onSubmit,
	onCancel,
	passwordLabel = "New password",
	confirmPasswordLabel = "Confirm new password",
	submitButtonText = "Save new password",
	cancelButtonText = "Cancel",
}: PasswordFormProps) {
	const isFormValid =
		newPassword.length >= 8 && newPassword === confirmNewPassword;

	return (
		<form onSubmit={onSubmit} className="space-y-4">
			<div className="space-y-2">
				<Label htmlFor="new-password">{passwordLabel}</Label>
				<div className="relative">
					<Input
						id="new-password"
						type={showNewPassword ? "text" : "password"}
						value={newPassword}
						onChange={(e) => onPasswordChange(e.target.value)}
						placeholder={`Enter a password (min. 8 characters)`}
						required
						autoFocus
						className="pr-10"
					/>
					<button
						type="button"
						aria-label="Toggle password visibility"
						className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
						onClick={onToggleShowPassword}
					>
						{showNewPassword ? (
							<EyeOff className="w-4 h-4" />
						) : (
							<Eye className="w-4 h-4" />
						)}
					</button>
				</div>
				{newPassword && newPassword.length < 8 && (
					<p className="text-xs text-orange-600">
						Password must be at least 8 characters long
					</p>
				)}
			</div>

			<div className="space-y-2">
				<Label htmlFor="confirm-new-password">{confirmPasswordLabel}</Label>
				<div className="relative">
					<Input
						id="confirm-new-password"
						type={showConfirmPassword ? "text" : "password"}
						value={confirmNewPassword}
						onChange={(e) => onConfirmPasswordChange(e.target.value)}
						placeholder="Re-enter your password"
						required
						className="pr-10"
					/>
					<button
						type="button"
						aria-label="Toggle password visibility"
						className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
						onClick={onToggleShowConfirmPassword}
					>
						{showConfirmPassword ? (
							<EyeOff className="w-4 h-4" />
						) : (
							<Eye className="w-4 h-4" />
						)}
					</button>
				</div>
				{confirmNewPassword && newPassword !== confirmNewPassword && (
					<p className="text-xs text-red-600">Passwords do not match</p>
				)}
			</div>

			{error && (
				<div className="p-3 bg-red-50 border border-red-200 rounded-lg">
					<p className="text-sm text-red-800">{error}</p>
				</div>
			)}

			<div className="flex gap-2 pt-2">
				<Button type="button" variant="ghost" onClick={onCancel}>
					{cancelButtonText}
				</Button>
				<Button
					type="submit"
					className="flex-1"
					disabled={isLoading || !isFormValid}
				>
					{isLoading ? "Saving..." : submitButtonText}
				</Button>
			</div>
		</form>
	);
}
